import { partById } from "../parts/PartCatalog";
import type { PlacedPart, RocketDesign } from "../parts/Rocket";

export interface FuelPools {
  liquid: number; // shared across all attached liquid tanks
  liquidCapacity: number;
  solidByUid: Map<string, number>; // solid boosters have their own fuel
}

export class Vessel {
  readonly attachedUids = new Set<string>();
  /** 2D position (m) in parent-body frame. */
  rx = 0;
  ry = 0;
  /** 2D velocity (m/s). */
  vx = 0;
  vy = 0;
  /** Orientation: angle (radians) where 0 means the rocket's nose points in +y direction. */
  heading = 0;
  /** Throttle 0..1 */
  throttle = 0;
  /** Active stage index — parts with stage <= activeStage are "armed"/firing. */
  activeStage = 0;
  /** Currently whichever body we're in the SOI of. */
  parentId = "earth";
  /** Aggregate dry mass of still-attached parts (kg). */
  dryMass = 0;
  /** Fuel tracking. */
  fuel: FuelPools = { liquid: 0, liquidCapacity: 0, solidByUid: new Map() };
  /** Peak temperature this flight (K) — for reentry warning. */
  peakTempK = 0;
  /** True once any parachute has been deployed. */
  parachuteDeployed = false;
  /** True if craft has landed safely (below a low-speed threshold). */
  landed = false;
  /** True if destroyed (crash/heat). */
  destroyed = false;
  /** Mission elapsed time (s). */
  met = 0;

  constructor(readonly design: RocketDesign) {
    for (const p of design.parts) this.attachedUids.add(p.uid);
    this.recomputeMass();
    this.fuel.liquid = this.fuel.liquidCapacity;
    for (const p of design.parts) {
      const d = partById(p.partId);
      if (d.fuelKind === "solid") this.fuel.solidByUid.set(p.uid, d.fuelCapacity ?? 0);
    }
  }

  get attachedParts(): PlacedPart[] {
    return this.design.parts.filter((p) => this.attachedUids.has(p.uid));
  }

  get hasControl() {
    return this.attachedParts.some((p) => partById(p.partId).isControl);
  }

  /** Mass in kg = dry + current fuel. */
  get mass() {
    let m = this.dryMass + this.fuel.liquid;
    for (const f of this.fuel.solidByUid.values()) m += f;
    return m;
  }

  recomputeMass() {
    this.dryMass = 0;
    this.fuel.liquidCapacity = 0;
    for (const p of this.attachedParts) {
      const d = partById(p.partId);
      this.dryMass += d.mass;
      if (d.fuelKind === "liquid") this.fuel.liquidCapacity += d.fuelCapacity ?? 0;
    }
    // Clamp pooled liquid to new capacity (after staging)
    this.fuel.liquid = Math.min(this.fuel.liquid, this.fuel.liquidCapacity);
  }

  /** Engines currently producing thrust. */
  activeEngines() {
    return this.attachedParts.filter((p) => {
      const d = partById(p.partId);
      if (!d.thrust) return false;
      if (p.stage > this.activeStage) return false;
      if (d.fuelKind === "solid") {
        return (this.fuel.solidByUid.get(p.uid) ?? 0) > 0;
      }
      return this.fuel.liquid > 0;
    });
  }

  /** Altitude above parent body surface. */
  altitude(bodyRadius: number) {
    return Math.hypot(this.rx, this.ry) - bodyRadius;
  }

  /** Return landing-gear-span half-width (m) if any legs are attached. */
  gearSpan() {
    let span = 0;
    for (const p of this.attachedParts) {
      const d = partById(p.partId);
      if (d.landingLegSpan) span = Math.max(span, d.landingLegSpan);
    }
    return span;
  }
}
